import { FC, ReactNode } from 'react';

import { Icon } from '@purpur/library/icon';
import { chevronRight } from '@purpur/library/icon/assets/chevron-right';

import { NamedIcon } from '@telialt/common-icon/src';
import { NavigationItem } from '@telialt/models/src/Navigation';
import { usePathname } from '@telialt/utils/src/pathname';

import { NAVIGATION_MAX_MENU_LEVELS } from '../../constants';
import { NAVIGATION_TEST_IDS } from '../../test-ids';
import { getIsLinkActive, getIsSelfService } from '../../utils/path';
import {
    MobileMenuItemBadge,
    MobileMenuItemContainer,
    MobileMenuItemContent,
    StyledMenuButton,
    StyledMenuLink,
} from './MobileMenu.styles';

type Props = {
    item: NavigationItem;
    menuLevel: number;
    onOpenChildrenClick?: () => void;
    customItemContent?: ReactNode;
    customIsActive?: boolean;
};

export const MobileMenuItemLink: FC<Props> = ({
    item,
    menuLevel,
    onOpenChildrenClick,
    customItemContent,
    customIsActive,
}) => {
    const pathname = usePathname();
    const isPartialMatch = getIsSelfService(pathname);
    const isLinkActive = Boolean(item.url && getIsLinkActive(item.url, pathname, isPartialMatch));
    const isActive = customIsActive ?? isLinkActive;

    const itemContent = (
        <MobileMenuItemContent>
            {item.title}
            {customItemContent}
            {item.icon && <NamedIcon name={item.icon} size="md" />}
            {item.badge && <MobileMenuItemBadge>{item.badge}</MobileMenuItemBadge>}
        </MobileMenuItemContent>
    );

    const handleLinkClick = () => {
        // Clearing modal open state to prevent automatically opening mobile menu in case user clicks on the link of the same page
        window.history.replaceState({}, '', window.location.href);
    };

    const hasChildren =
        (menuLevel < NAVIGATION_MAX_MENU_LEVELS &&
            item.children?.some((child) => child.displayOption !== 'Desktop only')) ??
        false;

    const itemLink = (
        <StyledMenuLink
            onClick={handleLinkClick}
            href={item.url}
            target={item.openInNewTab ? '_blank' : undefined}
            $colorOption={item.colorOption}
            $isActive={isActive}
            role="menuitem"
            rel={item.noFollow ? 'nofollow' : undefined}
            data-element="Link"
            data-element-name={item.title}
            data-element-color-option={item.colorOption}
        >
            {itemContent}
        </StyledMenuLink>
    );

    const itemChildrenButton = (
        <StyledMenuButton onClick={onOpenChildrenClick} role="menuitem">
            {itemContent}
            <Icon svg={chevronRight} size="md" />
        </StyledMenuButton>
    );

    return (
        <MobileMenuItemContainer $displayOption={item.displayOption} data-test={NAVIGATION_TEST_IDS.MOBILE_MENU_ITEM}>
            {hasChildren ? itemChildrenButton : itemLink}
        </MobileMenuItemContainer>
    );
};
